export interface UpcomingEscapeRowEligibilityInput {
  flightDataLoading: boolean
  flightUpsellEnabled: boolean
  hasExistingFlight: boolean
  carHireDataLoading: boolean
  carHireUpsellEnabled: boolean
  hasExistingCarHire: boolean
  insuranceDataLoading: boolean
  insuranceUpsellEnabled: boolean
  insuranceBookingProtectionDataLoading: boolean
  insuranceBookingProtectionUpsellEnabled: boolean
  tourDataLoading: boolean
  tourUpsellEnabled: boolean
  hasExistingTour: boolean
  airportTransferDataLoading: boolean
  airportTransferUpsellEnabled: boolean
  hasExistingTransfer: boolean
  hasFlightUpsell: boolean
  hasFlightTitle: boolean
  hasInsuranceUpsell: boolean
  hasInsuranceBookingProtectionUpsell: boolean
  hasTourUpsell: boolean
  hasAirportTransferUpsell: boolean
  hasCarHireUpsell: boolean
  transferBenefitEligibleForOrder: boolean
  hasLoungeBenefit: boolean
  loungeEligibleForOrder: boolean
  esimEnabled: boolean
  hasEsimBenefit: boolean
  esimBenefitListLoaded: boolean
  esimEligibleForOrder: boolean
  /** Order-level eSIM allowance present (mirrors LoyaltyOrderViewBenefitsEsimItem). */
  hasOrderEsimAllowance: boolean
  /** Trip end date already passed — eSIM row must not render. */
  isPastEsimTrip: boolean
  /** Loyalty eSIM order view is ready and the trip has not ended. */
  hasEsimBenefitView: boolean
}

export interface UpcomingEscapeRowEligibility {
  showFlight: boolean
  showCarHire: boolean
  showTravelProtection: boolean
  showCancellationProtection: boolean
  showTour: boolean
  showAirportTransfer: boolean
  showTransferBenefit: boolean
  showLounge: boolean
  showEsim: boolean
}

/**
 * Visibility gates that match UpcomingEscapeUpsellList: enabled/not-loading plus
 * the payload (or eSIM view) required to actually paint a row.
 * The parent mounts the card only when at least one of these flags is true.
 * CFMR loading may be timeout-unmasked by the upsells hook; the payload gate
 * still prevents an Add CTA before a catalog product exists.
 */
function getUpcomingEscapeRowEligibility(
  input: UpcomingEscapeRowEligibilityInput,
): UpcomingEscapeRowEligibility {
  return {
    showFlight: !input.flightDataLoading
      && input.flightUpsellEnabled
      && !input.hasExistingFlight
      && input.hasFlightUpsell
      && input.hasFlightTitle,
    showCarHire: !input.carHireDataLoading
      && input.carHireUpsellEnabled
      && !input.hasExistingCarHire
      && input.hasCarHireUpsell,
    showTravelProtection: !input.insuranceDataLoading
      && input.insuranceUpsellEnabled
      && input.hasInsuranceUpsell,
    showCancellationProtection: !input.insuranceDataLoading
      && !input.insuranceBookingProtectionDataLoading
      && !input.insuranceUpsellEnabled
      && input.insuranceBookingProtectionUpsellEnabled
      && input.hasInsuranceBookingProtectionUpsell,
    showTour: !input.tourDataLoading
      && input.tourUpsellEnabled
      && !input.hasExistingTour
      && input.hasTourUpsell,
    showTransferBenefit: input.transferBenefitEligibleForOrder,
    showAirportTransfer: !input.transferBenefitEligibleForOrder
      && !input.airportTransferDataLoading
      && input.airportTransferUpsellEnabled
      && !input.hasExistingTransfer
      && input.hasAirportTransferUpsell,
    showLounge: input.hasLoungeBenefit && input.loungeEligibleForOrder,
    showEsim: input.esimEnabled
      && input.hasEsimBenefit
      && input.esimBenefitListLoaded
      && input.esimEligibleForOrder
      && input.hasOrderEsimAllowance
      && !input.isPastEsimTrip
      && input.hasEsimBenefitView,
  }
}

export function hasUpcomingEscapeVisibleRows(eligibility: UpcomingEscapeRowEligibility): boolean {
  return eligibility.showFlight
    || eligibility.showCarHire
    || eligibility.showTravelProtection
    || eligibility.showCancellationProtection
    || eligibility.showTour
    || eligibility.showAirportTransfer
    || eligibility.showTransferBenefit
    || eligibility.showLounge
    || eligibility.showEsim
}

export function isUpcomingEscapeCommercialLoading(input: Pick<
  UpcomingEscapeRowEligibilityInput,
  | 'flightDataLoading'
  | 'carHireDataLoading'
  | 'insuranceDataLoading'
  | 'insuranceBookingProtectionDataLoading'
  | 'tourDataLoading'
  | 'airportTransferDataLoading'
>): boolean {
  return input.flightDataLoading
    || input.carHireDataLoading
    || input.insuranceDataLoading
    || input.insuranceBookingProtectionDataLoading
    || input.tourDataLoading
    || input.airportTransferDataLoading
}

export default getUpcomingEscapeRowEligibility
